// /**
//  * Products - list
//  */

// React
import React, { useContext } from 'react';

// UI
import theme from '../../../theme';
import {
  createStyles,
  makeStyles,
  Theme,
  withStyles,
} from '@material-ui/core/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
  Button,
  Grid,
} from '@material-ui/core';

// Icon
import Icon from '@mdi/react';
import {
  mdiChevronDown,
  mdiChevronUp,
  mdiPencil,
  mdiConsolidate,
  mdiPrinter,
  mdiClose,
} from '@mdi/js';

// Type
import { Variant } from '../interfaces';
import { Link, useHistory, useParams } from 'react-router-dom';
import Can from '../../../components/Can';
import AuthContext from '../../../authContext';

type Data = Variant;
type Props = {
  data: any;
  productName: string;
  handleOpenConfirmDisable: (id: number) => void;
  handleOpen: (id: number, type: string) => void;
  changeStatus: any;
};

type Ord = 'asc' | 'desc';

interface HeadCell {
  id: keyof Data;
  label: string;
}

interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  order: Ord;
  orderBy: string;
  rowCount: number;
}

// Style
const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      textAlign: 'left',
      backgroundColor: '#D5DADD !important',
      color: '#212121',
    },
    body: {
      textAlign: 'left',

      padding: '5px 8px 5px 24px !important',
      fontSize: 14,
    },
  })
)(TableCell);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    paper: {
      width: '100%',
    },
    table: {
      minWidth: 750,
    },
    columnText: {
      padding: '0 10px',
    },
    dot: {
      position: 'relative',
      display: 'inline-block',
      top: '0',
      left: '-8px',
      width: '8px',
      height: '8px',
      borderRadius: '50%',
    },
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
    hover: {
      '&:hover': {
        backgroundColor: '#F8F9FA !important',
      },
    },
  })
);

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Ord,
  orderBy: Key
): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string }
  ) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

const IconSort = (order: Ord) => {
  return (
    <Icon path={order === 'asc' ? mdiChevronDown : mdiChevronUp} size={1} />
  );
};

function EnhancedTableHead(props: EnhancedTableProps) {
  const authContext = useContext(AuthContext);
  const { order, orderBy, onRequestSort } = props;
  const classes = useStyles();
  const createSortHandler = (property: keyof Data) => (
    event: React.MouseEvent<unknown>
  ) => {
    onRequestSort(event, property);
  };

  let headCells: HeadCell[] = [];

  Can({
    role: authContext.user.role,
    perform: 'products:inventory',
    yes: () => {
      headCells = [
        // {
        //   id: 'productName',
        //   label: 'نام کالا',
        // },
        {
          id: 'id',
          label: 'شناسه تنوع کالا',
        },
        {
          id: 'code',
          label: 'کد تنوع کالا',
        },
        {
          id: 'sellerName',
          label: 'فروشنده',
        },
        {
          id: 'providerName',
          label: 'تامین کننده',
        },
        {
          id: 'inventory',
          label: 'موجودی',
        },
      ];
    },
    no: () => {
      headCells = [
        // {
        //   id: 'productName',
        //   label: 'نام کالا',
        // },
        {
          id: 'id',
          label: 'شناسه تنوع کالا',
        },
        {
          id: 'code',
          label: 'کد تنوع کالا',
        },
        {
          id: 'sellerName',
          label: 'فروشنده',
        },
        {
          id: 'providerName',
          label: 'تامین کننده',
        },
      ];
    },
  });

  return (
    <TableHead>
      <TableRow>
        <StyledTableCell />
        {headCells.map(headCell => {
          let orderStatus = orderBy === headCell.id ? order : 'asc';
          return (
            <StyledTableCell
              key={headCell.id}
              align="center"
              sortDirection={orderBy === headCell.id ? order : false}
              data-cy={headCell.id}
            >
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderStatus}
                onClick={createSortHandler(headCell.id)}
                IconComponent={() => IconSort(orderStatus)}
              >
                <span className={classes.columnText}>{headCell.label}</span>
              </TableSortLabel>
            </StyledTableCell>
          );
        })}
        <StyledTableCell />
      </TableRow>
    </TableHead>
  );
}

export default function EnhancedTable(props: Props) {
  const classes = useStyles();
  const [order, setOrder] = React.useState<Ord>('desc');
  const [orderBy, setOrderBy] = React.useState<keyof Data>('id');
  const [dense] = React.useState(false);
  const history = useHistory();
  const urlParams = useParams();
  const authContext = useContext(AuthContext);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const flatFeatures = (items: any) => {
    const features: string[] = [];
    if (items.features) {
      items.features.forEach((el: { key: string; value: string }) => {
        features.push(`${el.key}: ${el.value}`);
      });
    }
    return features;
  };

  return (
    <div className={classes.root}>
      <TableContainer>
        <Table
          className={classes.table}
          aria-labelledby="tableTitle"
          size={dense ? 'small' : 'medium'}
          aria-label="enhanced table"
        >
          <EnhancedTableHead
            classes={classes}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={props.data.length}
          />
          <TableBody>
            {stableSort(props.data, getComparator(order, orderBy))
              .slice()
              .map((row: any, index: number) => {
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow className={classes.hover} key={index}>
                    <StyledTableCell align="center">
                      {' '}
                      <span
                        className={classes.dot}
                        style={{
                          backgroundColor:
                            row.status === 'SUSPENDED' ||
                              row.status === 'REGISTERED'
                              ? theme.palette.error.main
                              : theme.palette.green.main,
                        }}
                      />
                    </StyledTableCell>
                    {/* <StyledTableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      align="center"
                    >
                      {row.productName}{' '}
                      {`${
                        row?.features?.length
                          ? ` - (` +
                            flatFeatures(row)?.join(',')?.slice(0, 20) +
                            ')'
                          : ''
                      }`}
                    </StyledTableCell> */}
                    <StyledTableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      align="center"
                      data-cy="tbid"
                    >
                      {row.id}
                    </StyledTableCell>
                    <StyledTableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      align="center"
                      data-cy="code"
                    >
                      {row.code}
                    </StyledTableCell>
                    <StyledTableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      align="center"
                      data-cy="sellerName"
                    >
                      {row.sellerCode
                        ? `${row.sellerName}(${row.sellerCode})`
                        : row.sellerName}
                    </StyledTableCell>
                    <StyledTableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      align="center"
                      data-cy="providerName"
                    >
                      {row.providerCode
                        ? `${row.providerName}(${row.providerCode})`
                        : row.providerName}
                    </StyledTableCell>
                    <Can
                      role={authContext.user.role}
                      perform="products:inventory"
                      yes={() => (
                        <StyledTableCell
                          component="th"
                          id={labelId}
                          scope="row"
                          align="center"
                        >
                          {row.stock}
                        </StyledTableCell>
                      )}
                    />
                    <StyledTableCell style={{ textAlign: 'center' }}>
                      <IconButton
                        title="جزئیات و نوع ها"
                        size="small"
                        onClick={() =>
                          history.push(
                            `/your-products/${urlParams.id}/variant/${row.id}`
                          )
                        }
                        data-cy="detail"
                      >
                        <img
                          src={require('../../../assets/images/file-document-outline-copy-2.png')}
                          alt="detail"
                        />
                      </IconButton>
                      {/* <IconButton
                        title="موجودی"
                        size="small"
                        onClick={() =>
                          history.push(
                            `/skus/inventory/${row.id}/${props.productName}`
                          )
                        }
                        data-cy="inventoryBtn"
                      >
                        <img
                          src={require('../../../assets/images/boxes.png')}
                          alt="inventory"
                        />
                      </IconButton> */}
                      <Link to={`/orders/variantOrders/${row.id}`}>
                        <IconButton title="لیست" size="small">
                          <img
                            src={require('../../../assets/images/list.png')}
                            alt="list"
                          />
                        </IconButton>
                      </Link>
                      <Can
                        perform="variant:edit"
                        role={authContext.user.role}
                        yes={() => (
                          <Link
                            to={`/variant-edit/${row.id}`}
                            data-cy="variantEdit"
                          >
                            <IconButton title="ویرایش" size="small">
                              <Icon
                                path={mdiPencil}
                                size={1}
                                color={theme.palette.blue.main}
                              />
                            </IconButton>
                          </Link>
                        )}
                      />
                      {authContext.user.role !== 'SELLER_ADMIN' && (
                        <IconButton
                          title="تاریخچه ورود و خروج"
                          size="small"
                          onClick={() => props.handleReport(row.id)}
                        >
                          <Icon
                            path={mdiConsolidate}
                            size={1}
                            color={theme.palette.blue.main}
                          />
                        </IconButton>
                      )}
                      <IconButton
                        title="حذف تنوع"
                        size="small"
                        onClick={() => props.handleDelete(row.id)}
                      >
                        <Icon
                          path={mdiClose}
                          size={1}
                          color={'red'}
                        />
                      </IconButton>
                    </StyledTableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
